import type { ReactNode, CSSProperties } from "react";

/**
 * Icon set:
 *   - Generic status kinds (ok/error/warning/info/...) — semantic coarse buckets.
 *   - Domain kinds (kiosk… / paid… / ticket… / box… / replenish…) — preserve legacy PNG identity while
 *     staying theme-aware. Each combines a domain silhouette with a small corner status badge.
 *
 * Themed via --color-* tokens. Corner badges use hardcoded badge colors that survive any theme
 * so the semantic meaning (red = bad, green = ok) is stable across colorblind modes too.
 */
export type StatusIconKind =
  // generic
  | "ok" | "okLight" | "warning" | "error" | "info" | "unknown"
  | "pending" | "progress" | "paused" | "blocked" | "stop"
  | "event" | "faultyTransaction"
  // kiosk
  | "kioskOk" | "kioskError" | "kioskManager" | "kioskWarning" | "kioskUnknown" | "kioskSomeError"
  // payment
  | "paidOk" | "paidDenied" | "paidOpen" | "paidIssue" | "paidNot" | "paidLate"
  // ticket
  | "ticketNew" | "ticketOpen" | "ticketMonitor" | "ticketAuto" | "ticketDone" | "ticketFault"
  // box / invapp
  | "boxDone" | "boxMoving" | "boxPickedUp" | "boxPacked" | "boxPacking" | "boxWaiting"
  // replenish
  | "replenishOrdered" | "replenishPartial1" | "replenishPartial2" | "replenishDone"
  // button / action icons
  | "mailRead" | "mailUnread" | "cogEdit" | "cogDelete" | "solved" | "unsolved"
  // chrome / UI icons (inherit currentColor)
  | "gear" | "wrench" | "close" | "chevronDown" | "chevronRight" | "arrowUp" | "arrowDown" | "search" | "star" | "starFilled"
  | "help" | "moreHorizontal"
  | "plus" | "minus" | "check" | "alert"
  // entity icons
  | "user" | "package" | "pin" | "monitor"
  // misc UI
  | "grid" | "hash" | "menu" | "chevronLeft" | "grip" | "clock"
  // grid-row action icons
  | "mail" | "view" | "push" | "edit" | "trash"
  // files
  | "file" | "download";

const colorToken: Record<StatusIconKind, string> = {
  ok:       "var(--color-success)",
  okLight:  "var(--color-success-light, color-mix(in srgb, var(--color-success) 55%, white))",
  warning:  "var(--color-warning)",
  error:    "var(--color-error)",
  info:     "var(--color-info)",
  unknown:  "var(--color-text-muted)",
  pending:  "var(--color-text-muted)",
  progress: "var(--color-info)",
  paused:   "var(--color-warning)",
  blocked:  "var(--color-error)",
  stop:     "var(--color-error)",
  event:    "var(--color-warning)",
  faultyTransaction: "var(--color-info)",
  // Domain families share the domain's silhouette color (neutral text), corner badge colored separately.
  kioskOk: "var(--color-text)",       kioskError: "var(--color-text)", kioskManager: "var(--color-text)",
  kioskWarning: "var(--color-text)",  kioskUnknown: "var(--color-text)", kioskSomeError: "var(--color-text)",
  paidOk: "var(--color-success)",     paidDenied: "var(--color-error)", paidOpen: "var(--color-success-light, color-mix(in srgb, var(--color-success) 55%, white))",
  paidIssue: "var(--color-error)",    paidNot: "var(--color-warning)",  paidLate: "var(--color-warning)",
  ticketNew: "var(--color-info)",     ticketOpen: "var(--color-info)",  ticketMonitor: "var(--color-warning)",
  ticketAuto: "var(--color-text-muted)", ticketDone: "var(--color-success)", ticketFault: "var(--color-error)",
  boxDone: "var(--color-success)",    boxMoving: "var(--color-info)",   boxPickedUp: "var(--color-info)",
  boxPacked: "var(--color-info)",     boxPacking: "var(--color-info)",  boxWaiting: "var(--color-text-muted)",
  replenishOrdered: "var(--color-info)", replenishPartial1: "var(--color-info)", replenishPartial2: "var(--color-info)",
  replenishDone: "var(--color-success)",
  // button / action
  mailRead:   "var(--color-text-secondary)",
  mailUnread: "var(--color-text)",
  cogEdit:    "var(--color-text-secondary)",
  cogDelete:  "var(--color-text-secondary)",
  solved:     "var(--color-success)",
  unsolved:   "var(--color-error)",
  // UI chrome — inherit from surrounding context
  gear:         "currentColor",
  wrench:       "currentColor",
  close:        "currentColor",
  chevronDown:  "currentColor",
  chevronRight: "currentColor",
  arrowUp:      "currentColor",
  arrowDown:    "currentColor",
  search:       "currentColor",
  star:         "currentColor",
  starFilled:   "currentColor",
  help:         "currentColor",
  moreHorizontal: "currentColor",
  plus:         "currentColor",
  minus:        "currentColor",
  check:        "currentColor",
  alert:        "currentColor",
  user:         "currentColor",
  package:      "currentColor",
  pin:          "currentColor",
  monitor:      "currentColor",
  grid:         "currentColor",
  hash:         "currentColor",
  menu:         "currentColor",
  chevronLeft:  "currentColor",
  grip:         "currentColor",
  clock:        "currentColor",
  mail:         "currentColor",
  view:         "currentColor",
  push:         "currentColor",
  edit:         "currentColor",
  trash:        "currentColor",
  file:         "currentColor",
  download:     "currentColor",
};

/* ── Reusable bases & badges ───────────────────────────── */

// Kiosk: screen (rounded rect) + base/stand.
// Original glyph spanned y 2.5 → 16.5 (centroid 9.5) — 0.5 unit above the
// viewBox centre. Shifted all absolute Y coords by +0.5 so the full glyph
// centres at y = 10.
const kioskBase = (
  <>
    <rect x="3" y="3" width="14" height="11" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 14v2.5M13 14v2.5M5 17h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
);

// $ disk
const dollarBase = (
  <>
    <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 5.5v9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M12.5 7.8c-.4-.7-1.3-1.1-2.5-1.1-1.4 0-2.5.7-2.5 1.6 0 1.9 5 1 5 3 0 .9-1.1 1.6-2.5 1.6-1.2 0-2.1-.4-2.5-1.1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </>
);

// Ticket tag — rounded rect with a hole near the top-left
const ticketBase = (
  <>
    <path d="M3 6.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="6" cy="7.5" r="1" fill="currentColor" />
    <path d="M3 10h14" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
  </>
);

// Box — isometric-ish cube
const boxBase = (
  <>
    <path d="M10 2.5l6.5 3.2v8l-6.5 3.3-6.5-3.3v-8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3.5 5.7l6.5 3.3 6.5-3.3M10 9v8" stroke="currentColor" strokeWidth="1.3" />
  </>
);

// Refresh ring (open/partial/full)
function refreshRing(fraction: number) {
  // fraction 0..1 of ring filled. 0 = dashed outline. 1 = solid ring + check.
  if (fraction === 0) {
    return (
      <>
        <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.4 2" />
        <path d="M13.5 5.5 L17 8.5 L14 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </>
    );
  }
  // Use stroke-dasharray to show a fraction of the circle circumference.
  const circumference = 2 * Math.PI * 7;
  const dash = circumference * fraction;
  return (
    <>
      <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.35" />
      <circle
        cx="10" cy="10" r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 10 10)"
      />
    </>
  );
}

// Corner badges — small overlay in top-right corner
function cornerBadge(kind: "check" | "x" | "bang" | "q" | "plus" | "gear" | "eye" | "clock" | "dots" | "arrow" | "seal" | "slash", color: string) {
  const cx = 15, cy = 5, r = 3.8;
  // Common: filled circle backdrop in badge color
  const bg = <circle cx={cx} cy={cy} r={r} fill={color} stroke="var(--color-bg)" strokeWidth="0.8" />;
  const fg = "white";
  switch (kind) {
    case "check":
      return (<>{bg}<path d={`M${cx - 1.8} ${cy} l1.4 1.4 l2.4 -2.6`} fill="none" stroke={fg} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></>);
    case "x":
      return (<>{bg}<path d={`M${cx - 1.6} ${cy - 1.6} l3.2 3.2 M${cx + 1.6} ${cy - 1.6} l-3.2 3.2`} stroke={fg} strokeWidth="1.2" strokeLinecap="round" /></>);
    case "bang":
      return (<>{bg}<path d={`M${cx} ${cy - 1.8} v2.4`} stroke={fg} strokeWidth="1.2" strokeLinecap="round" /><circle cx={cx} cy={cy + 1.6} r={0.7} fill={fg} /></>);
    case "q":
      return (<>{bg}<path d={`M${cx - 1.2} ${cy - 0.8} c0.4 -1.2 2 -1.2 2.4 0 c 0.2 0.8 -1.2 1 -1.2 1.8`} fill="none" stroke={fg} strokeWidth="1.1" strokeLinecap="round" /><circle cx={cx} cy={cy + 1.7} r={0.6} fill={fg} /></>);
    case "plus":
      return (<>{bg}<path d={`M${cx - 1.6} ${cy} h3.2 M${cx} ${cy - 1.6} v3.2`} stroke={fg} strokeWidth="1.2" strokeLinecap="round" /></>);
    case "gear":
      return (<>{bg}<circle cx={cx} cy={cy} r={1.2} fill="none" stroke={fg} strokeWidth="1" /><path d={`M${cx} ${cy - 2.6} v1 M${cx} ${cy + 1.6} v1 M${cx - 2.6} ${cy} h1 M${cx + 1.6} ${cy} h1`} stroke={fg} strokeWidth="0.9" strokeLinecap="round" /></>);
    case "eye":
      return (<>{bg}<ellipse cx={cx} cy={cy} rx="2.3" ry="1.4" fill="none" stroke={fg} strokeWidth="1" /><circle cx={cx} cy={cy} r={0.9} fill={fg} /></>);
    case "clock":
      return (<>{bg}<circle cx={cx} cy={cy} r={2.3} fill="none" stroke={fg} strokeWidth="0.9" /><path d={`M${cx} ${cy - 1.3} v1.5 l1 0.6`} stroke={fg} strokeWidth="0.9" strokeLinecap="round" fill="none" /></>);
    case "dots":
      return (<>{bg}<circle cx={cx - 1.6} cy={cy} r={0.7} fill={fg} /><circle cx={cx} cy={cy} r={0.7} fill={fg} /><circle cx={cx + 1.6} cy={cy} r={0.7} fill={fg} /></>);
    case "arrow":
      return (<>{bg}<path d={`M${cx - 1.8} ${cy} h3.4 M${cx + 0.4} ${cy - 1.4} l2 1.4 l-2 1.4`} fill="none" stroke={fg} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></>);
    case "seal":
      return (<>{bg}<path d={`M${cx - 2} ${cy} l1.5 1.5 l3 -3`} fill="none" stroke={fg} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></>);
    case "slash":
      return (<>{bg}<path d={`M${cx - 2.4} ${cy + 2.4} l4.8 -4.8`} stroke={fg} strokeWidth="1.4" strokeLinecap="round" /></>);
  }
}

/* ── Generic glyphs ────────────────────────────────────── */

const infoGlyph = (
  <>
    <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M10 9v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="10" cy="6.3" r="0.9" fill="currentColor" />
  </>
);

const glyphs: Record<StatusIconKind, ReactNode> = {
  ok: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6 10.5l2.5 2.5 5-5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  okLight: infoGlyph,
  warning: (
    <>
      <path d="M10 2.5l8 14.5H2z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M10 8v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="10" cy="14.5" r="0.9" fill="currentColor" />
    </>
  ),
  error: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  info: infoGlyph,
  unknown: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7.8 7.5c.3-1.3 1.4-2 2.5-2 1.4 0 2.4 1 2.4 2.2 0 1.8-2.5 2-2.5 3.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10.2" cy="14.5" r="0.9" fill="currentColor" />
    </>
  ),
  pending: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="6.5" cy="10" r="1" fill="currentColor" />
      <circle cx="10" cy="10" r="1" fill="currentColor" />
      <circle cx="13.5" cy="10" r="1" fill="currentColor" />
    </>
  ),
  progress: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeDasharray="3.2 2.4" />
      <path d="M10 5.5v4.8l3 1.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  paused: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.2 6.5v7M11.8 6.5v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  blocked: (
    <path
      d="M18 10a8 8 0 1 1-16 0a8 8 0 1 1 16 0z M4 9h12v2H4z"
      fill="currentColor"
      fillRule="evenodd"
    />
  ),
  stop: (
    <path
      d="M17.4 6.94 L17.4 13.06 L13.06 17.4 L6.94 17.4 L2.6 13.06 L2.6 6.94 L6.94 2.6 L13.06 2.6 Z"
      fill="currentColor"
    />
  ),
  event: (
    // 4-point star centred on the viewBox (y 3.5 → 16.5 in a 0–20 box) so the
    // glyph's geometric middle lines up with the SVG middle; the earlier
    // path was 2 units above centre, leaving the icon visually high in rows.
    <path
      d="M10 3.5l1.7 4.8 4.8 1.7-4.8 1.7L10 16.5l-1.7-4.8-4.8-1.7 4.8-1.7z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinejoin="round"
    />
  ),
  faultyTransaction: (
    <>
      <path d="M2.5 8 A 7.5 7.5 0 0 1 16.5 4.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13.8 2 L17 4.8 L14.5 8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.5 12 A 7.5 7.5 0 0 1 3.5 15.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.2 18 L3 15.2 L5.5 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <g stroke="var(--color-success)" strokeWidth="1.5" strokeLinecap="round" fill="none">
        <path d="M10 6.5v7" />
        <path d="M12.3 8.2c-.4-.6-1.2-1-2.3-1-1.3 0-2.3.6-2.3 1.4 0 1.7 4.6.9 4.6 2.7 0 .8-1 1.4-2.3 1.4-1.1 0-1.9-.4-2.3-1" />
      </g>
    </>
  ),

  // ── Kiosk domain ──
  kioskOk:        (<>{kioskBase}{cornerBadge("check", "var(--color-success)")}</>),
  kioskError:     (<>{kioskBase}{cornerBadge("x",     "var(--color-error)")}</>),
  kioskManager:   (<>{kioskBase}{cornerBadge("gear",  "var(--color-warning)")}</>),
  kioskWarning:   (<>{kioskBase}{cornerBadge("bang",  "var(--color-warning)")}</>),
  kioskUnknown:   (<>{kioskBase}{cornerBadge("q",     "var(--color-text-muted)")}</>),
  kioskSomeError: (<>{kioskBase}{cornerBadge("dots",  "var(--color-warning)")}</>),

  // ── Payment domain ──
  paidOk:     (<>{dollarBase}{cornerBadge("check", "var(--color-success)")}</>),
  paidDenied: (<>{dollarBase}{cornerBadge("x",     "var(--color-error)")}</>),
  paidOpen:   (<>{dollarBase}{cornerBadge("arrow", "var(--color-info)")}</>),
  paidIssue:  (<>{dollarBase}{cornerBadge("bang",  "var(--color-error)")}</>),
  paidNot:    (<>{dollarBase}{cornerBadge("slash", "var(--color-warning)")}</>),
  paidLate:   (<>{dollarBase}{cornerBadge("clock", "var(--color-warning)")}</>),

  // ── Ticket domain ──
  ticketNew:     (<>{ticketBase}{cornerBadge("plus",  "var(--color-info)")}</>),
  ticketOpen:    (<>{ticketBase}{cornerBadge("arrow", "var(--color-info)")}</>),
  ticketMonitor: (<>{ticketBase}{cornerBadge("eye",   "var(--color-warning)")}</>),
  ticketAuto:    (<>{ticketBase}{cornerBadge("gear",  "var(--color-text-muted)")}</>),
  ticketDone:    (<>{ticketBase}{cornerBadge("check", "var(--color-success)")}</>),
  ticketFault:   (<>{ticketBase}{cornerBadge("bang",  "var(--color-error)")}</>),

  // ── Box / InvApp domain ──
  boxDone:     (<>{boxBase}{cornerBadge("check", "var(--color-success)")}</>),
  boxMoving:   (<>{boxBase}{cornerBadge("arrow", "var(--color-info)")}</>),
  boxPickedUp: (<>{boxBase}{cornerBadge("plus",  "var(--color-info)")}</>),
  boxPacked:   (<>{boxBase}{cornerBadge("seal",  "var(--color-info)")}</>),
  boxPacking:  (<>{boxBase}{cornerBadge("dots",  "var(--color-info)")}</>),
  boxWaiting:  (<>{boxBase}{cornerBadge("clock", "var(--color-text-muted)")}</>),

  // ── Replenish domain ──
  replenishOrdered:   refreshRing(0),
  replenishPartial1:  refreshRing(0.33),
  replenishPartial2:  refreshRing(0.66),
  replenishDone: (
    <>
      {refreshRing(1)}
      {cornerBadge("check", "var(--color-success)")}
    </>
  ),

  // ── Button / action icons ──

  // Open envelope — letter read. Flap lifted, letter peeking out.
  mailRead: (
    <>
      {/* Envelope body */}
      <rect x="2" y="7" width="16" height="10.5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      {/* Open flap folded back */}
      <path d="M2 8.5l8-5.5 8 5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Letter peeking out */}
      <rect x="5" y="4.5" width="10" height="6" rx="0.5" fill="var(--color-bg)" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 7h6M7 9h4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </>
  ),
  // Closed envelope — unread.
  // Original y 5 → 17 (centroid 11) sat 1 unit below centre; shifted -1 so
  // the envelope centres at y = 10.
  mailUnread: (
    <>
      <rect x="2" y="4" width="16" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 4l8 6.5L18 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </>
  ),
  // Pencil on document — edit action (matches legacy cog-edit.png which had a document frame).
  cogEdit: (
    <>
      {/* Document / page frame */}
      <rect x="3" y="2" width="11" height="16" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 6h5M6 9h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      {/* Pencil overlay in bottom-right */}
      <path
        d="M11 14.5l4.3-4.3a1 1 0 0 1 1.4 0l.6.6a1 1 0 0 1 0 1.4L13 16.5H11z"
        fill="var(--color-bg)"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </>
  ),
  // Trash can — delete action.
  // Original y 4 → 17.4 (centroid 10.7) sat 0.7 unit below centre; shifted
  // every absolute Y by -0.7 so the glyph centres at y = 10.
  cogDelete: (
    <>
      <path d="M4.5 5.3h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 3.3h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M5.5 5.3l.8 10.5a1 1 0 0 0 1 .9h5.4a1 1 0 0 0 1-.9L14.5 5.3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.5 8.3v5M11.5 8.3v5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  // Speech bubble with check — solved/resolved (green).
  // Bubble originally y 4 → 17 (tail tip at 17), centroid 10.5. Every
  // absolute Y shifted -0.5 so the glyph centres at y = 10.
  solved: (
    <>
      <path d="M3 3.5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H7l-3.5 3V13.5H3a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7 8.5l2.5 2.5L14 6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  // Speech bubble with ! — unsolved/needs attention (red).
  // Same -0.5 Y shift as `solved` for identical centring.
  unsolved: (
    <>
      <path d="M3 3.5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H7l-3.5 3V13.5H3a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 6v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="10.3" r="0.8" fill="currentColor" />
    </>
  ),

  // ── UI chrome icons ──
  gear: (
    <>
      <circle cx="10" cy="10" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M10 2.5v2.1 M10 15.4v2.1 M2.5 10h2.1 M15.4 10h2.1 M4.7 4.7l1.5 1.5 M13.8 13.8l1.5 1.5 M4.7 15.3l1.5-1.5 M13.8 6.2l1.5-1.5"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      />
    </>
  ),
  // Wrench — Lucide-style wrench icon; used for Tools
  wrench: (
    <>
      <path
        d="M15.3 3.3a4 4 0 0 0-5.1 5.1L4 14.6a1.7 1.7 0 1 0 2.4 2.4l6.2-6.2a4 4 0 0 0 5.1-5.1l-2.3 2.3-1.6-1.6 2.3-2.3z"
        fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
      />
    </>
  ),
  close: (
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
  ),
  chevronDown: (
    <path d="M5 7l5 6 5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  chevronRight: (
    <path d="M7 5l6 5-6 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  arrowUp: (
    <path d="M10 16V4M5 9l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  arrowDown: (
    <path d="M10 4v12M5 11l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  search: (
    <>
      <circle cx="9" cy="9" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  // Star: original y 1.8 → 19.4 (centroid 10.6) sat 0.6 unit below centre.
  // Every y shifted -0.6 so the star centres at y = 10.
  star: (
    <polygon
      points="10 1.2 12.6 7.2 19 8 14.2 12.4 15.4 18.8 10 15.6 4.6 18.8 5.8 12.4 1 8 7.4 7.2"
      fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
    />
  ),
  starFilled: (
    <polygon
      points="10 1.2 12.6 7.2 19 8 14.2 12.4 15.4 18.8 10 15.6 4.6 18.8 5.8 12.4 1 8 7.4 7.2"
      fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
    />
  ),
  // Help — "?" in circle; inline tooltip hint next to form labels.
  help: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.8 7.8c.3-1.3 1.4-2 2.5-2 1.4 0 2.4 1 2.4 2.2 0 1.8-2.5 2-2.5 3.8" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="10.2" cy="14.3" r="0.8" fill="currentColor" />
    </>
  ),
  // Three horizontal dots — overflow / "more" menu trigger.
  moreHorizontal: (
    <>
      <circle cx="4.5" cy="10" r="1.6" fill="currentColor" />
      <circle cx="10" cy="10" r="1.6" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1.6" fill="currentColor" />
    </>
  ),
  plus: (
    <path d="M10 4v12M4 10h12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  ),
  minus: (
    <path d="M4 10h12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  ),
  check: (
    <path d="M4 10.5l3.5 3.5 8-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  alert: (
    <>
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="10" cy="13.5" r="0.9" fill="currentColor" />
    </>
  ),
  // Person silhouette — head + shoulders
  user: (
    <>
      <circle cx="10" cy="7" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 17.5a6.5 6.5 0 0 1 13 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  // Package — box with seam line
  package: (
    <>
      <path d="M10 2.5l6.5 3.2v8l-6.5 3.3-6.5-3.3v-8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M3.5 5.7l6.5 3.3 6.5-3.3M10 9v8" stroke="currentColor" strokeWidth="1.3" />
    </>
  ),
  // Map pin — teardrop with hole
  pin: (
    <>
      <path d="M10 2.5c3.3 0 6 2.6 6 5.8 0 4.4-6 9.2-6 9.2s-6-4.8-6-9.2c0-3.2 2.7-5.8 6-5.8z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="10" cy="8.3" r="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </>
  ),
  // Monitor / kiosk silhouette — reuse the kiosk base
  monitor: kioskBase,
  // 2x2 grid — used as the "Bulk select" affordance in identity filters
  grid: (
    <>
      <rect x="3" y="3" width="6" height="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="11" y="3" width="6" height="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3" y="11" width="6" height="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="11" y="11" width="6" height="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </>
  ),
  // # — hash mark; used for identity-id contexts
  hash: (
    <path d="M7.5 2.5l-2 15M14.5 2.5l-2 15M2.5 7.5h15M2.5 12.5h15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  ),
  // ☰ — three horizontal lines; hamburger menu
  menu: (
    <path d="M3 5h14M3 10h14M3 15h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  ),
  // < — chevron pointing left
  chevronLeft: (
    <path d="M13 5l-6 5 6 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  ),
  // 6-dot grip handle (drag indicator)
  grip: (
    <>
      <circle cx="7" cy="5" r="1.4" fill="currentColor" />
      <circle cx="13" cy="5" r="1.4" fill="currentColor" />
      <circle cx="7" cy="10" r="1.4" fill="currentColor" />
      <circle cx="13" cy="10" r="1.4" fill="currentColor" />
      <circle cx="7" cy="15" r="1.4" fill="currentColor" />
      <circle cx="13" cy="15" r="1.4" fill="currentColor" />
    </>
  ),
  // Analog clock
  clock: (
    <>
      <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6v4l2.5 1.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  // Envelope — mail / send-email actions
  mail: (
    <>
      <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.5 5l7.5 6 7.5-6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  // Eye — view / preview action
  view: (
    <>
      <path d="M2 10s2.5-5 8-5 8 5 8 5-2.5 5-8 5-8-5-8-5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="10" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </>
  ),
  // Upload arrow — push / send action
  push: (
    <>
      <path d="M17.5 12.5v3.5a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5v-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="14 6.5 10 2.5 6 6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="2.5" x2="10" y2="13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  // Pencil — edit action
  edit: (
    <>
      <path d="M10 17h7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M13.5 3.5a1.7 1.7 0 0 1 2.5 2.5L6 16.5l-3 .5.5-3 10-10.5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </>
  ),
  // Trash bin — delete action
  trash: (
    <>
      <polyline points="3 5.5 5 5.5 17 5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M15.5 5.5l-1 11.5a1.5 1.5 0 0 1-1.5 1.5H7a1.5 1.5 0 0 1-1.5-1.5l-1-11.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8 5V3.5A1 1 0 0 1 9 2.5h2a1 1 0 0 1 1 1V5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <line x1="8" y1="9" x2="8" y2="15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="12" y1="9" x2="12" y2="15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  // Document with a folded corner - a file
  file: (
    <>
      <path d="M11.5 2.5H5.5a1.5 1.5 0 0 0-1.5 1.5v12a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5V7z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M11.5 2.5V7H16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M7 11h6M7 14h4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  // Download arrow into a tray - the counterpart of push
  download: (
    <>
      <path d="M17.5 12.5v3.5a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5v-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="6 9 10 13 14 9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="13" x2="10" y2="2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
};

interface Props {
  kind: StatusIconKind;
  label?: string;
  size?: number;
  style?: CSSProperties;
  /** Optional class on the wrapping <span> — for callers that need to
   *  participate in their own SCSS module's sizing/spacing rules. */
  className?: string;
}

export function StatusIcon({ kind, label, size = 16, style, className }: Props) {
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={className}
      style={{ display: "inline-flex", verticalAlign: "middle", color: colorToken[kind], lineHeight: 0, ...style }}
    >
      <svg viewBox="0 0 20 20" width={size} height={size}>{glyphs[kind]}</svg>
    </span>
  );
}

/**
 * Every kind `StatusIcon` can actually draw, read off the glyph table rather than restated.
 *
 * `glyphs` is a `Record<StatusIconKind, ReactNode>`, so the compiler already guarantees it is
 * exhaustive over the union — which makes its keys the authoritative list at runtime. The icon
 * gallery checks its own curated display order against this; a kind added here but not there is
 * simply never looked at, and nothing else would report it.
 */
export const STATUS_ICON_KINDS = Object.keys(glyphs) as StatusIconKind[];
